import React, { useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as OpenInNewIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  PersonOutline as CreatorIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonButton, CommonChip, toast } from "../../../../Components/ui";
import {
  getExplorerAddressUrl,
  EXPLORER_LABEL,
} from "../../../../../static/Constants";

const LotteryHeroCard = ({
  id,
  lottery,
  gameTypeConfig,
  statusConfig,
  currency,
  prizeInCurrency,
  isOpen,
  playLink,
  onRefresh,
  refreshing,
}) => {
  const history = useHistory();
  const [copiedKey, setCopiedKey] = useState(null);

  const TypeIcon = gameTypeConfig.icon;

  const handleCopy = (text, key, label = "Address") => {
    if (!text) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
      toast.success(`${label} copied to clipboard!`);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator
        .share({
          title: `AutoBET Lottery #${id} - ${gameTypeConfig.name}`,
          text: `Check out Lottery #${id} on AutoBET! Total prize pool: ${prizeInCurrency} ${currency}`,
          url: url,
        })
        .catch(() => handleCopy(url, "share-link", "Lottery link"));
    } else {
      handleCopy(url, "share-link", "Lottery link");
    }
  };

  const ownerAddress = lottery?.ownerAddress || "";
  const explorerUrl = getExplorerAddressUrl(ownerAddress);

  return (
    <Card
      className="lottery-hero-glow"
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        position: "relative",
      }}
    >
      {/* Top Gradient Stripe Indicator */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: gameTypeConfig.gradient || colors.gradients.primary,
        }}
      />

      <Grid container spacing={3} alignItems="center">
        {/* Left Side: Lottery Meta & Badges */}
        <Grid item xs={12} md={7} lg={8}>
          <Stack spacing={1.5}>
            {/* Badges row */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              {TypeIcon && (
                <Chip
                  icon={
                    <TypeIcon
                      sx={{
                        fontSize: "1.1rem !important",
                        color: `${gameTypeConfig.color} !important`,
                      }}
                    />
                  }
                  label={gameTypeConfig.name}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                    bgcolor: gameTypeConfig.bgColor,
                    color: gameTypeConfig.color,
                    border: `1px solid ${gameTypeConfig.color}30`,
                    px: 0.5,
                  }}
                />
              )}
              <CommonChip
                label={statusConfig.label}
                variant={statusConfig.variant}
                size="small"
                sx={{ fontWeight: 700 }}
              />
              {isOpen && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.25,
                    borderRadius: borderRadiusPx.full,
                    bgcolor: colors.emerald[50],
                    border: `1px solid ${colors.emerald[200]}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: colors.emerald[500],
                      boxShadow: `0 0 6px ${colors.emerald[400]}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.emerald[700],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                    }}
                  >
                    Live On-Chain
                  </Typography>
                </Box>
              )}
            </Stack>

            {/* Main Title */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: colors.slate[900],
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                textAlign: "left",
              }}
            >
              Lottery #{id}
            </Typography>

            {/* Creator / Owner Address info */}
            {ownerAddress && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ color: colors.slate[500] }}
                >
                  <CreatorIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Created by:
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    color: colors.slate[800],
                    fontWeight: 600,
                    bgcolor: colors.slate[100],
                    px: 1,
                    py: 0.25,
                    borderRadius: borderRadiusPx.sm,
                    border: `1px solid ${colors.slate[200]}`,
                  }}
                >
                  {ownerAddress}
                </Typography>

                <Tooltip
                  title={
                    copiedKey === "owner-addr"
                      ? "Copied!"
                      : "Copy Creator Address"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleCopy(ownerAddress, "owner-addr", "Creator Address")
                    }
                    sx={{
                      p: 0.5,
                      color: colors.slate[400],
                      "&:hover": {
                        color: colors.slate[800],
                        bgcolor: colors.slate[100],
                      },
                    }}
                  >
                    {copiedKey === "owner-addr" ? (
                      <CheckIcon
                        sx={{ fontSize: 16, color: colors.success.main }}
                      />
                    ) : (
                      <CopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>

                {explorerUrl && (
                  <Tooltip title={EXPLORER_LABEL}>
                    <IconButton
                      size="small"
                      component="a"
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        p: 0.5,
                        color: colors.slate[400],
                        "&:hover": {
                          color: colors.primary.main,
                          bgcolor: colors.blue[50],
                        },
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Stack>
        </Grid>

        {/* Right Side: Prize Pool & Actions */}
        <Grid item xs={12} md={5} lg={4}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: borderRadiusPx.lg,
              bgcolor: gameTypeConfig.bgColor || colors.blue[50],
              border: `1px solid ${gameTypeConfig.borderColor || colors.blue[200]}`,
              textAlign: { xs: "left", md: "right" },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[600],
                fontWeight: 700,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Total Prize Pool
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.4rem" },
                background: gameTypeConfig.gradient || "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mt: 0.5,
                mb: 2,
              }}
            >
              {prizeInCurrency} {currency}
            </Typography>

            {/* Quick Action Button Group */}
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent={{ xs: "flex-start", md: "flex-end" }}
              alignItems="center"
              flexWrap="wrap"
            >
              <Tooltip title="Share Lottery Link">
                <IconButton
                  onClick={handleShare}
                  sx={{
                    bgcolor: "#ffffff",
                    color: colors.slate[600],
                    border: `1px solid ${colors.slate[200]}`,
                    "&:hover": {
                      bgcolor: colors.slate[100],
                      color: colors.slate[900],
                    },
                  }}
                >
                  <ShareIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Refresh On-Chain Data">
                <IconButton
                  onClick={onRefresh}
                  disabled={refreshing}
                  sx={{
                    bgcolor: "#ffffff",
                    color: colors.slate[600],
                    border: `1px solid ${colors.slate[200]}`,
                    "&:hover": {
                      bgcolor: colors.slate[100],
                      color: colors.slate[900],
                    },
                  }}
                >
                  <RefreshIcon
                    sx={{
                      fontSize: 18,
                      animation: refreshing
                        ? "spin 1s linear infinite"
                        : "none",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>

              {isOpen && (
                <CommonButton
                  variant="playNow"
                  onClick={() => history.push(playLink)}
                  sx={{
                    background: gameTypeConfig.gradient || colors.gradients.primary,
                    boxShadow: gameTypeConfig.boxShadow,
                    "&:hover": {
                      background: gameTypeConfig.gradient || colors.gradients.primary,
                      boxShadow: `0 6px 20px ${gameTypeConfig.glow || "rgba(0,0,0,0.2)"}`,
                      transform: "translateY(-1px)",
                    },
                    flexGrow: { xs: 1, sm: 0 },
                  }}
                >
                  Play Now
                </CommonButton>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

LotteryHeroCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  lottery: PropTypes.object,
  gameTypeConfig: PropTypes.object.isRequired,
  statusConfig: PropTypes.object.isRequired,
  currency: PropTypes.string.isRequired,
  prizeInCurrency: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  playLink: PropTypes.string.isRequired,
  onRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool,
};

export default LotteryHeroCard;
